import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faArrowRight,
  faArrowTrendDown,
  faArrowTrendUp,
  faBell,
  faBuildingColumns,
  faCalendarDays,
  faChampagneGlasses,
  faChartLine,
  faCheck,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faCircle,
  faCircleInfo,
  faClock,
  faClockRotateLeft,
  faCoins,
  faCopy,
  faCrown,
  faDice,
  faEnvelope,
  faFire,
  faGavel,
  faGear,
  faGripVertical,
  faHouse,
  faLeaf,
  faLock,
  faPaperPlane,
  faPenNib,
  faPercent,
  faPiggyBank,
  faPlus,
  faQuestion,
  faReceipt,
  faRightFromBracket,
  faRotateLeft,
  faShieldHalved,
  faSliders,
  faTrashCan,
  faTriangleExclamation,
  faUnlockKeyhole,
  faUser,
  faUserPlus,
  faUserSlash,
  faWallet,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import {
  faBell as faBellRegular,
  faCalendarDays as faCalendarDaysRegular,
  faClock as faClockRegular,
  faEnvelope as faEnvelopeRegular,
  faTrashCan as faTrashCanRegular,
  faUser as faUserRegular,
} from "@fortawesome/free-regular-svg-icons";

// Icon names mirror the Font Awesome Free glyphs used across the Claude
// Design mockups (project/screens-v2.jsx, screens-v3-*.jsx) so the icon
// choices stay traceable back to the designs.
const SOLID = {
  "calendar-days": faCalendarDays,
  coins: faCoins,
  copy: faCopy,
  user: faUser,
  check: faCheck,
  question: faQuestion,
  plus: faPlus,
  "arrow-right": faArrowRight,
  circle: faCircle,
  "chevron-down": faChevronDown,
  "chevron-left": faChevronLeft,
  "chevron-right": faChevronRight,
  "right-from-bracket": faRightFromBracket,
  "pen-nib": faPenNib,
  "paper-plane": faPaperPlane,
  "unlock-keyhole": faUnlockKeyhole,
  lock: faLock,
  gear: faGear,
  bell: faBell,
  "shield-halved": faShieldHalved,
  "user-plus": faUserPlus,
  "user-slash": faUserSlash,
  clock: faClock,
  "clock-rotate-left": faClockRotateLeft,
  "grip-vertical": faGripVertical,
  "trash-can": faTrashCan,
  crown: faCrown,
  dice: faDice,
  gavel: faGavel,
  "rotate-left": faRotateLeft,
  "circle-info": faCircleInfo,
  envelope: faEnvelope,
  sliders: faSliders,
  fire: faFire,
  house: faHouse,
  leaf: faLeaf,
  "triangle-exclamation": faTriangleExclamation,
  xmark: faXmark,
  // The finance area: the tab itself, the two flow directions, and one glyph
  // per ledger category (see finance/ledger.ts CATEGORY_LABELS).
  "chart-line": faChartLine,
  "arrow-trend-up": faArrowTrendUp,
  "arrow-trend-down": faArrowTrendDown,
  wallet: faWallet,
  "building-columns": faBuildingColumns,
  percent: faPercent,
  "champagne-glasses": faChampagneGlasses,
  receipt: faReceipt,
  "piggy-bank": faPiggyBank,
} satisfies Record<string, IconDefinition>;

const REGULAR: Partial<Record<keyof typeof SOLID, IconDefinition>> = {
  "calendar-days": faCalendarDaysRegular,
  user: faUserRegular,
  bell: faBellRegular,
  clock: faClockRegular,
  envelope: faEnvelopeRegular,
  "trash-can": faTrashCanRegular,
};

export type IconGlyph = keyof typeof SOLID;

export function Icon({
  name,
  variant = "solid",
  size,
  className,
}: {
  name: IconGlyph;
  variant?: "solid" | "regular";
  size?: number;
  className?: string;
}) {
  const def = (variant === "regular" && REGULAR[name]) || SOLID[name];
  return (
    <FontAwesomeIcon
      icon={def}
      style={size ? { fontSize: size, lineHeight: 1 } : undefined}
      className={className}
    />
  );
}

export function Dot({ size = 6, className = "" }: { size?: number; className?: string }) {
  return <Icon name="circle" size={size} className={className} />;
}

export function GlyphLabel({
  icon,
  children,
  gap = "gap-2",
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  gap?: string;
}) {
  return (
    <span className={`inline-flex items-center ${gap}`}>
      {icon}
      <span>{children}</span>
    </span>
  );
}
